import { shuffle } from 'lodash-es';
import { useQuery } from 'react-query';
import { useEffect, useState } from 'react';
import { getChurnSurvey } from '../api/get-churn-survey';
import type { SurveyModel, SurveyAnswerModel } from '../api/survey-model';
import type {
  ChurnSurveyQuestions,
  DowngradeReasonQuestion,
  SurveyAnswerWithNextIndicator,
} from '../types';

export enum QUESTIONS_INDEXES {
  DOWNGRADE_REASON = 0,
  DOWNGRADE_EXPLANATION_START = 1,
  DOWNGRADE_EXPLANATION_END = 9,
  MORE_DETAILS = 10,
}

type SurveyParsedAnswers = SurveyAnswerModel[] | SurveyAnswerWithNextIndicator[];

/**
 * Shuffles all the answers and keeps the last one at its fixed place.
 * This is implemented based on the fact that the last answer is always
 * 'Other (please specify)'.
 * @param answers
 * @param shouldHaveFixedAnswer If the last answer should be fixed at its place or not
 */
const shuffleAnswers = (
  answers: SurveyParsedAnswers = [],
  shouldHaveFixedAnswer = true,
): SurveyParsedAnswers => {
  const fixedAnswer = answers.pop();

  return shouldHaveFixedAnswer && fixedAnswer !== undefined
    ? [...shuffle(answers), fixedAnswer]
    : shuffle(answers);
};

/**
 * Get answers from the API and prepare them to be rendered in the churn form.
 */
export function useChurnSurveyQuestions(): {
  questions?: ChurnSurveyQuestions;
  formConfigData?: SurveyModel;
} {
  const [questions, setQuestions] = useState<ChurnSurveyQuestions | undefined>();
  const { data: formConfigData } = useQuery({
    queryKey: 'churn-survey-query',
    queryFn: () => getChurnSurvey(),
  });

  useEffect(() => {
    if (formConfigData === undefined) {
      return;
    }

    const questionsArray = formConfigData.survey.content.questions;

    const downgradeReason: DowngradeReasonQuestion = {
      ...questionsArray[QUESTIONS_INDEXES.DOWNGRADE_REASON],
      answers: questionsArray[QUESTIONS_INDEXES.DOWNGRADE_REASON].answers?.map((answer, index) => {
        return {
          ...answer,
          nextForAnswer:
            questionsArray[QUESTIONS_INDEXES.DOWNGRADE_REASON].nextByAnswer &&
            questionsArray[QUESTIONS_INDEXES.DOWNGRADE_REASON].nextByAnswer[index].replace(
              'question:',
              '',
            ),
        };
      }),
    };
    const downgradeExplanations = questionsArray.slice(
      QUESTIONS_INDEXES.DOWNGRADE_EXPLANATION_START,
      QUESTIONS_INDEXES.DOWNGRADE_EXPLANATION_END + 1,
    );
    const moreDetails = questionsArray[QUESTIONS_INDEXES.MORE_DETAILS];

    setQuestions({
      downgradeReason: {
        ...downgradeReason,
        answers: shuffleAnswers(downgradeReason.answers, false),
      },
      downgradeExplanations: downgradeExplanations.map((question) => {
        return {
          ...question,
          answers: shuffleAnswers(question.answers),
        };
      }),
      moreDetails,
    });
  }, [formConfigData]);

  return { questions, formConfigData };
}
